// 학습 적격/제외 데이터셋 큐레이션 — 05-admin §6.2.
// GET /v1/admin/training-eligibility (목록) + PUT (적격/제외 결정, 01 §3.M, moderator).
// training_eligibility 테이블(01 §2): variant_id / eligible / reason / region / curated_by / updated_at.
import { api, errMsg, isNotReady } from "../core/api.js";
import { h, mount, notReadyState } from "../core/dom.js";
import type { Page, TrainingEligibility } from "../models.js";

const EXCLUDE_REASONS = ["opt_out", "no_consent", "sensitive", "region"] as const;
const REASON_LABEL: Record<string, string> = {
  opt_out: "opt-out (권리유보)",
  no_consent: "동의 없음",
  sensitive: "민감정보 (Art.9)",
  region: "region opt-in 미충족",
};

function fmtTime(ms: number): string {
  return new Date(ms).toLocaleString("ko-KR");
}

function isEligible(t: TrainingEligibility): boolean {
  return t.eligible === true || t.eligible === 1;
}

export async function trainingView(_p: Record<string, string>, root: HTMLElement): Promise<void> {
  const listWrap = h("div", { class: "card" });
  const formStatus = h("p", { class: "small muted" }, "");

  // 큐레이션 입력 폼 (적격/제외 결정 → PUT).
  const variantField = h("input", { class: "field", placeholder: "variant_id" }) as HTMLInputElement;
  const eligibleSelect = h(
    "select",
    { class: "field" },
    h("option", { value: "eligible" }, "적격 (eligible)"),
    h("option", { value: "excluded" }, "제외 (excluded)"),
  ) as HTMLSelectElement;
  const reasonSelect = h(
    "select",
    { class: "field" },
    h("option", { value: "" }, "제외 사유 (제외 시)"),
    ...EXCLUDE_REASONS.map((r) => h("option", { value: r }, REASON_LABEL[r] ?? r)),
  ) as HTMLSelectElement;
  const regionField = h("input", { class: "field", placeholder: "region (예: KR / EU)" }) as HTMLInputElement;

  async function curate(): Promise<void> {
    const variantId = variantField.value.trim();
    if (!variantId) {
      formStatus.textContent = "variant_id를 입력하세요.";
      formStatus.className = "small error";
      return;
    }
    const eligible = eligibleSelect.value === "eligible";
    formStatus.textContent = "저장 중…";
    formStatus.className = "small muted";
    try {
      // PUT /v1/admin/training-eligibility (01 §3.M, moderator) — 큐레이터 결정 → 06-ai 소비.
      await api.put("/v1/admin/training-eligibility", {
        variant_id: variantId,
        eligible,
        reason: !eligible ? reasonSelect.value || undefined : undefined,
        region: regionField.value.trim() || undefined,
      });
      formStatus.textContent = `저장됨: ${variantId} → ${eligible ? "적격" : "제외"} (감사 로그 기록).`;
      formStatus.className = "small ok";
      void load();
    } catch (e) {
      formStatus.textContent = isNotReady(e)
        ? "엔드포인트 준비중 (/v1/admin/training-eligibility PUT 미구현)."
        : `저장 실패: ${errMsg(e)}`;
      formStatus.className = "small error";
    }
  }

  async function load(): Promise<void> {
    listWrap.replaceChildren(h("div", { class: "state muted" }, "적격성 목록 불러오는 중…"));
    try {
      const page = await api.get<Page<TrainingEligibility> | TrainingEligibility[]>(
        "/v1/admin/training-eligibility?limit=50",
      );
      const items = Array.isArray(page) ? page : (page.items ?? []);
      if (items.length === 0) {
        listWrap.replaceChildren(h("div", { class: "state muted" }, "큐레이션된 항목이 없습니다."));
        return;
      }
      const table = h(
        "table",
        { class: "tbl" },
        h(
          "thead",
          {},
          h(
            "tr",
            {},
            h("th", {}, "variant"),
            h("th", {}, "적격성"),
            h("th", {}, "제외 사유"),
            h("th", {}, "region"),
            h("th", {}, "갱신"),
          ),
        ),
        h(
          "tbody",
          {},
          ...items.map((t) =>
            h(
              "tr",
              {},
              h("td", {}, h("span", { class: "mono small" }, t.variant_id)),
              h(
                "td",
                {},
                h("span", { class: `badge ${isEligible(t) ? "ok" : "danger"}` }, isEligible(t) ? "적격" : "제외"),
              ),
              h("td", { class: "small" }, t.reason ? (REASON_LABEL[t.reason] ?? t.reason) : "—"),
              h("td", { class: "muted small" }, t.region ?? "—"),
              h("td", { class: "muted small" }, t.updated_at ? fmtTime(t.updated_at) : "—"),
            ),
          ),
        ),
      );
      listWrap.replaceChildren(table);
    } catch (e) {
      if (isNotReady(e)) {
        listWrap.replaceChildren(notReadyState("학습 적격 목록 (/v1/admin/training-eligibility) 미구현."));
        return;
      }
      listWrap.replaceChildren(h("div", { class: "state error" }, `목록 불러오기 실패: ${errMsg(e)}`));
    }
  }

  mount(
    root,
    h(
      "div",
      { class: "stack" },
      h("h1", {}, "학습 적격 큐레이션"),
      h(
        "p",
        { class: "muted" },
        "콘텐츠를 학습 적격(eligible)/제외(excluded)로 분류합니다 (05-admin §6.2). opt-out/DSR(§4.6) 전파 결과가 일관되게 반영됩니다. 학습셋 구성·실행은 06-ai.",
      ),
      h(
        "section",
        { class: "card" },
        h("h2", {}, "적격성 결정"),
        h("div", { class: "row wrap" }, variantField, eligibleSelect, reasonSelect, regionField),
        h("div", { class: "row" }, h("button", { class: "btn", onclick: () => void curate() }, "저장 (PUT)")),
        formStatus,
      ),
      listWrap,
    ),
  );
  await load();
}
